import { useMemo, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import AppShell from "@/components/feature/app/AppShell";
import PdfBrandProvider from "@/components/feature/PdfBrandProvider";
import { ImmeublesProvider } from "@/pages/proprietaire/immeubles/ImmeublesProvider";
import { BiensProvider } from "@/pages/proprietaire/biens/BiensProvider";
import { LocatairesProvider } from "@/pages/proprietaire/locataires/LocatairesProvider";
import { ContratsProvider } from "@/pages/proprietaire/contrats/ContratsProvider";
import { PaiementsProvider } from "@/pages/proprietaire/paiements/PaiementsProvider";
import { LoyersProvider } from "@/pages/proprietaire/loyers/LoyersProvider";
import { DepensesProvider } from "@/pages/proprietaire/depenses/DepensesProvider";
import { MaintenanceProvider } from "@/pages/proprietaire/maintenance/MaintenanceProvider";
import { DocumentsProvider } from "@/pages/proprietaire/documents/DocumentsProvider";
import { AbonnementProvider, useAbonnement } from "@/pages/proprietaire/abonnement/AbonnementProvider";
import { getFormule, toEspaceFormules } from "@/pages/proprietaire/abonnement/types";
import { useFormules } from "@/store/formules/FormulesProvider";
import { ParametresProvider, useParametres } from "@/pages/proprietaire/parametres/ParametresProvider";
import { EmployesProvider, useEmployes } from "@/pages/agence/employes/EmployesProvider";
import { RolesProvider } from "@/store/permissions/RolesProvider";
import { OWNER_NAV_GROUPS, OWNER_USER, filterOwnerNavGroups } from "@/config/proprietaireNav";
import type { AppUser } from "@/config/appNav";
import { useSession } from "@/session/SessionProvider";
import { fullName } from "@/session/types";
import { initialsFromName, type PdfBrand } from "@/utils/pdfBrand";

const OWNER_ACCOUNT_LINKS = [
  { label: "Profil", to: "/proprietaire/parametres", icon: "ri-user-line" },
  { label: "Abonnement", to: "/proprietaire/abonnement", icon: "ri-vip-diamond-line", permission: "parametres.voir" },
  { label: "Mes paiements", to: "/proprietaire/mes-paiements", icon: "ri-history-line", permission: "parametres.voir" },
  { label: "Paramètres", to: "/proprietaire/parametres", icon: "ri-settings-3-line", permission: "parametres.voir" },
];

function OwnerPdfBrand({ children }: { children: ReactNode }) {
  const { profil } = useParametres();
  const brand = useMemo<PdfBrand>(() => {
    const name = [profil.prenom, profil.nom].filter(Boolean).join(" ").trim() || OWNER_USER.name;
    return {
      name,
      subtitle: "Espace Propriétaire",
      logo: profil.logo || undefined,
      initials: initialsFromName(name),
      contact: {
        email: profil.email,
        phone: profil.telephone,
        address: profil.adresse,
        website: profil.siteWeb,
        ifu: profil.ifu,
        rccm: profil.rccm,
      },
    };
  }, [profil]);

  return <PdfBrandProvider brand={brand}>{children}</PdfBrandProvider>;
}

function OwnerShell() {
  const { abonnement } = useAbonnement();
  const { session } = useSession();
  const { can } = useEmployes();
  // Tarifs et limites lus dans Supabase (source de vérité) : les modifications
  // du Super Admin sont reflétées ici automatiquement.
  const { formules: catalogueFormules } = useFormules();
  const formules = useMemo(() => toEspaceFormules(catalogueFormules), [catalogueFormules]);
  const formule = getFormule(abonnement.formuleId, formules);
  const formuleAffichee = getFormule(abonnement.formuleAfficheeId, formules);
  const limites = formule.limites;
  const immeubleHint = limites.immeubles === null ? "Illimité" : `${limites.immeubles} immeuble${limites.immeubles > 1 ? "s" : ""}`;
  const bienHint = limites.biens === null ? "Illimité" : `${limites.biens} bien${limites.biens > 1 ? "s" : ""}`;
  const userName = session ? fullName(session) : OWNER_USER.name;
  const user: AppUser = session
    ? { name: userName, role: session.fonction, initials: initialsFromName(userName) }
    : OWNER_USER;

  const groups = useMemo(() => filterOwnerNavGroups(OWNER_NAV_GROUPS, can), [can]);
  const accountLinks = useMemo(
    () => OWNER_ACCOUNT_LINKS.filter((link) => !link.permission || can(link.permission)),
    [can]
  );

  // Un employé d'une AGENCE ne doit pas accéder à l'espace Propriétaire.
  if (session?.role === "employe_agence" && session.scope !== "proprietaire") {
    return <Navigate to="/agence" replace />;
  }

  return (
    <AppShell
      groups={groups}
      user={user}
      spaceLabel="Espace Propriétaire"
      fallbackTitle="Tableau de bord"
      accountLinks={accountLinks}
      planLabel={`Formule ${formuleAffichee.nom}`}
      planHint={abonnement.paiementRequis ? "Paiement requis" : `${immeubleHint} · ${bienHint}`}
      planTo="/proprietaire/abonnement"
    />
  );
}

export default function ProprietaireLayout() {
  return (
    <EmployesProvider>
      <RolesProvider>
        <ImmeublesProvider>
          <BiensProvider>
            <LocatairesProvider>
              <ContratsProvider>
                <PaiementsProvider>
                  <LoyersProvider>
                    <DepensesProvider>
                      <MaintenanceProvider>
                        <DocumentsProvider>
                          <AbonnementProvider>
                            <ParametresProvider>
                              <OwnerPdfBrand>
                                <OwnerShell />
                              </OwnerPdfBrand>
                            </ParametresProvider>
                          </AbonnementProvider>
                        </DocumentsProvider>
                      </MaintenanceProvider>
                    </DepensesProvider>
                  </LoyersProvider>
                </PaiementsProvider>
              </ContratsProvider>
            </LocatairesProvider>
          </BiensProvider>
        </ImmeublesProvider>
      </RolesProvider>
    </EmployesProvider>
  );
}